import type {
  GetApiV1ThreeLetterCodeClientAdminsUserBulkCreationRequests200,
  GetApiV1ThreeLetterCodeClientAdminsUsers200One,
} from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date/formatDate';
import { convertDateTime } from '~/utils/dateTime';
import type { useUserList } from '../../hooks';
import type { UserListPresentation } from '../../presentations';

type UseUserListProps = ReturnType<typeof useUserList>['state'];
type PresentationProps = React.ComponentProps<typeof UserListPresentation>;

type Args = {
  usersData: GetApiV1ThreeLetterCodeClientAdminsUsers200One;
  bulkCreationData: GetApiV1ThreeLetterCodeClientAdminsUserBulkCreationRequests200;
  /** 環境フィーチャーフラグ（staging / development 環境で true） */
  enableStaging: UseUserListProps['enableStaging'];
  /** PartnerFeatureFlag 取得レスポンス */
  partnerFeatureFlagData: UseUserListProps['partnerFeatureFlagData'];
};

type IConvertToPresentationProps = (params: Args) => {
  rows: PresentationProps['rows'];
  uploadStatusRows: PresentationProps['uploadStatusRows'];
  showScheduledAlert: PresentationProps['showScheduledAlert'];
  showInternalColumns: PresentationProps['showInternalColumns'];
  showDownloadActivationCode: PresentationProps['showDownloadActivationCode'];
};

/**
 * API 型（snake_case）を UserList Presentation の props 型（camelCase）に変換する。
 *
 * 入力の `enableStaging` / `partnerFeatureFlags` は `useUserList` の state 型を、
 * 出力の各フィールドは `UserListPresentation` の Props 型を参照することで、
 * フック・変換・表示の 3 者間で型の単一情報源を保ち、型乖離が発生しないようにしている。
 * pagerProps / uploadStatusPagerProps の組み立ては Container 側で generatePagerProps を使って行う。
 *
 * アクティベーションコード関連の表示可否（showInternalColumns / showDownloadActivationCode）も、
 * フィーチャーフラグから導出してここで組み立てる。
 * showInternalColumns と showDownloadActivationCode はいずれも
 * 「activation_code フラグ有効 または enableStaging」で決まるため同値になる。
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  usersData,
  bulkCreationData,
  enableStaging,
  partnerFeatureFlagData,
}) => {
  const users = usersData.users;

  const rows: PresentationProps['rows'] = users.map((user, index) => ({
    id: user.id,
    no: index + 1,
    email: user.email,
    name: user.full_name,
    isActivated: user.is_activated,
    activationCode: user.activation_code ?? undefined,
    branchName: user.branch?.name ?? '',
    branchCode: user.branch?.code ?? '',
    billableFrom: user.billable_from
      ? formatDate({
          date: new Date(`${user.billable_from}T00:00:00`),
          formatType: 'yyyy/M/d',
        })
      : '-',
    createdAt: convertDateTime(user.created_at),
  }));

  const bulkCreationRequests = bulkCreationData.user_bulk_creation_requests;

  const uploadStatusRows: PresentationProps['uploadStatusRows'] = bulkCreationRequests.map(
    request => ({
      id: request.id,
      status: request.status,
      receptNo: String(request.id),
      errorContent: request.error_messages?.join(', '),
      scheduledAt: convertDateTime(request.scheduled_at ?? 0),
      registeredCount: request.target_user_count,
    })
  );

  const showScheduledAlert = bulkCreationRequests.some(r => r.status === 'registered');

  // activation_code フラグが有効なテナントでアクティベーションコード UI を表示する。
  // かつてはテナントコードのハードコード（BYM/LEF/SHL）で制御していたが、
  // 対象テナントを FeatureFlag で管理できるよう本判定へ移行した（MMT-156）。
  // development / staging 環境では enableStaging を OR 条件として加えることで、
  // フラグ未設定でも開発者・QA が UI を確認できる。
  // production / demo では activation_code フラグの判定結果のみで制御される。
  const partnerFeatureFlags = partnerFeatureFlagData.partner_feature_flags;
  const showActivationCodeUi =
    partnerFeatureFlags.some(flag => flag.feature_key === 'activation_code' && flag.enabled) ||
    enableStaging;

  return {
    rows,
    uploadStatusRows,
    showScheduledAlert,
    showInternalColumns: showActivationCodeUi,
    showDownloadActivationCode: showActivationCodeUi,
  };
};
